'use client';

import { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '@/context/AuthContext';
import { useCreateArticleAction } from '@/hooks/useCreateArticleAction';
import {
  DocumentPlusIcon,
  PencilSquareIcon,
  ArrowPathIcon,
} from '@heroicons/react/24/outline';

import { apiClient } from '@/app/api/client';
import React from 'react';
import PublishingCalendar from './PublishingCalendar';
import ContentPipeline from './ContentPipeline';
import PerformancePlaceholder from './PerformancePlaceholder';

// Data interfaces
interface BusinessStats {
  articlesCreated: number;
  articlesEdited: number;
  aiEdits: number;
}

interface Article {
  id: string;
  title: string;
  slug: string;
  publicId?: string;
  status: 'draft' | 'published' | 'scheduled';
  author: string;
  updatedAt?: number;
  contentUpdatedAt?: number;
  scheduledAt?: number;
}

export default function UserDashboard() {
  const { user } = useAuth();
  const [refreshKey, setRefreshKey] = useState(0);
  const handleRefresh = useCallback(() => {
    setRefreshKey(prev => prev + 1);
  }, []);

  useCreateArticleAction(handleRefresh);

  const timeRanges = [
    { id: 'billingCycle', label: 'Current Billing Cycle' },
    { id: 'today', label: 'Today' },
    { id: 'week', label: 'Past Week' },
    { id: 'month', label: 'Past 30 Days' }
  ];

  const [selectedTimeRange, setSelectedTimeRange] = useState<'today' | 'week' | 'month' | 'billingCycle'>('month');
  const [dateRangeLabel, setDateRangeLabel] = useState('');

  const [businessStats, setBusinessStats] = useState<BusinessStats | null>(null);
  const [allArticles, setAllArticles] = useState<Article[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [isFetchingRange, setIsFetchingRange] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Calculate article counts by status
  const articleCounts = useMemo(() => {
    return {
      draft: allArticles.filter(a => a.status === 'draft').length,
      scheduled: allArticles.filter(a => a.status === 'scheduled').length,
      published: allArticles.filter(a => a.status === 'published').length,
    };
  }, [allArticles]);

  useEffect(() => {
    const fetchDashboardData = async () => {
      if (isInitialLoad) {
        setIsLoading(true);
      } else {
        setIsFetchingRange(true);
      }
      setError(null);

      try {
        const response = await apiClient.get(`/api/dashboard/stats?range=${selectedTimeRange}`);
        if (!response.data) {
          console.error('Failed to fetch dashboard stats');
          setError('Failed to load dashboard data');
          return;
        }

        const data = response.data;
        setBusinessStats(data.stats);
        setAllArticles(data.recentArticles || []);

        // Set date range label
        if (selectedTimeRange === 'billingCycle') {
          if (data.period && data.period.start && data.period.end) {
            const startDate = new Date(data.period.start * 1000).toLocaleDateString();
            const endDate = new Date(data.period.end * 1000).toLocaleDateString();
            setDateRangeLabel(`${startDate} - ${endDate}`);
          } else {
            setDateRangeLabel('Current Billing Cycle');
          }
        } else {
          const today = new Date();
          let startDate = new Date();
          if (selectedTimeRange === 'today') {
            startDate.setHours(0, 0, 0, 0);
          } else if (selectedTimeRange === 'week') {
            startDate.setDate(today.getDate() - 7);
          } else if (selectedTimeRange === 'month') {
            startDate.setDate(today.getDate() - 30);
          }
          setDateRangeLabel(`${startDate.toLocaleDateString()} - ${today.toLocaleDateString()}`);
        }
      } catch (error) {
        console.error('Failed to fetch dashboard data:', error);
        setError('Failed to load dashboard data');
      }

      if (isInitialLoad) {
        setIsLoading(false);
        setIsInitialLoad(false);
      }
      setIsFetchingRange(false);
    };

    fetchDashboardData();
  }, [selectedTimeRange, refreshKey, isInitialLoad]);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 animate-pulse">
          <div className="bg-white shadow rounded-lg p-4 sm:p-6 mb-4 h-24"></div>
          <div className="bg-gray-200 h-16 rounded-lg mb-4"></div>
          <div className="bg-gray-200 h-96 rounded-lg mb-4"></div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="bg-gray-200 h-96 rounded-lg"></div>
            <div className="bg-gray-200 h-96 rounded-lg"></div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-500 text-lg">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Welcome Section with Activity Stats */}
        <div className="bg-white shadow rounded-lg p-6 mb-6">
          <div className="flex flex-col space-y-6">
            {/* Header with Time Range Filters */}
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center">
              <div>
                <h1 className="text-2xl font-bold text-gray-900">
                  Welcome, {user?.displayName || user?.email?.split('@')[0] || 'User'}
                </h1>
                <p className="text-gray-600 mt-2">Your content command center</p>
              </div>
              <div className="mt-4 md:mt-0">
                <div className="flex items-center space-x-4">
                  <div className="flex space-x-2 text-sm">
                    {timeRanges.map(range => (
                      <React.Fragment key={range.id}>
                        {range.id === 'today' && <div className="border-l border-gray-300 h-6 mx-2"></div>}
                        <button
                          onClick={() => setSelectedTimeRange(range.id as 'today' | 'week' | 'month' | 'billingCycle')}
                          className={`px-3 py-1 rounded-md ${selectedTimeRange === range.id
                            ? 'bg-blue-600 text-white'
                            : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                            }`}
                          disabled={isFetchingRange}
                        >
                          {range.label}
                        </button>
                      </React.Fragment>
                    ))}
                  </div>
                  {isFetchingRange && (
                    <div className="flex items-center text-sm text-gray-500">
                      <svg className="animate-spin h-4 w-4 mr-2 text-blue-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"></path>
                      </svg>
                      Updating…
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Activity Stats */}
            <div className="border-t border-gray-200 pt-6">
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-medium text-gray-500">Activity ({dateRangeLabel})</h3>
                <div className="flex items-center space-x-8">
                  <div className="flex items-center space-x-2">
                    <DocumentPlusIcon className="h-5 w-5 text-blue-500" />
                    <div className="text-left">
                      <p className="text-xs text-gray-500">Created</p>
                      <p className="text-lg font-semibold text-gray-900">{businessStats?.articlesCreated ?? 0}</p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    <PencilSquareIcon className="h-5 w-5 text-green-500" />
                    <div className="text-left">
                      <p className="text-xs text-gray-500">Edited</p>
                      <p className="text-lg font-semibold text-gray-900">{businessStats?.articlesEdited ?? 0}</p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    <ArrowPathIcon className="h-5 w-5 text-purple-500" />
                    <div className="text-left">
                      <p className="text-xs text-gray-500">AI Edits</p>
                      <p className="text-lg font-semibold text-gray-900">{businessStats?.aiEdits ?? 0}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Publishing Calendar */}
        <div className="mb-6">
          <PublishingCalendar articles={allArticles} />
        </div>

        {/* Content Pipeline & Performance */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
          <ContentPipeline
            draftCount={articleCounts.draft}
            scheduledCount={articleCounts.scheduled}
            publishedCount={articleCounts.published}
          />
          <PerformancePlaceholder />
        </div>
      </div>
    </div>
  );
}